/**
 * 権限付与モーダル: 1 人のユーザーの所属権限グループを複数選択して保存する (権限管理 SBI-8)。
 *
 * - 保存は `PUT /users/{user_sub}/permission-groups` (置き換え)。グループを 1 つも選ばないと所属をすべて外す
 *   (= 権限なし)。このときは保存前に「実行してよろしいですか」を確認する。
 * - 選んだグループの機能の和集合 (= 実際に利用できる機能) をプレビュー表示する。
 * - 最後の管理者から管理権限を外すとバックエンドが 409 で拒否する。理由と対処をバナーに示し、モーダルは開いたままにする。
 * - 保存前に、他者が同じユーザーの所属を変更していないか確認する (Issue #765、`guardedUpdate`)。
 */
import { useCallback, useMemo, useState } from 'react';
import type React from 'react';

import { ApiError } from '../api/common';
import type { AdminUser, PermissionFeature, PermissionGroup } from '../api/types';
import type { UsersClient } from '../api/users';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { featureLabels, userDisplayName } from '../permissions/adminLabels';
import type { RemoteChangeState } from '../utils/remoteChange';

import { AdminFormModal } from './AdminFormModal';
import { describeAdminError } from './permissionAdminMessages';

/** グループ ID の集合を順序に依存しない文字列にする (変更有無の判定用)。 */
function idsKey(ids: readonly string[]): string {
  return [...ids].sort().join(',');
}

export interface PermissionAssignModalProps {
  user: AdminUser;
  /** 選択肢にする権限グループ (名称順) */
  groups: readonly PermissionGroup[];
  /** 機能カタログ (プレビューの表示名) */
  features: readonly PermissionFeature[];
  apiClient: UsersClient;
  /** 保存成功時に呼ばれる */
  onSaved: (user: AdminUser, groupCount: number) => void;
  /** 編集中に他者がユーザーを削除していたとき、お知らせを閉じた後に呼ばれる */
  onDeletedByOthers: () => void;
  onClose: () => void;
}

export function PermissionAssignModal(props: PermissionAssignModalProps): React.JSX.Element {
  const { user, groups, features, apiClient, onSaved, onDeletedByOthers, onClose } = props;

  /** 開いたときの所属 (変更有無と他者の変更を判定する基準) */
  const [initialIds] = useState<string[]>(() => user.permission_groups.map((g) => g.group_id));
  const [selected, setSelected] = useState<string[]>(initialIds);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { confirmExecute, confirmDiscard, guardedUpdate, confirmDialog } = useConfirmDialog();

  const dirty = idsKey(selected) !== idsKey(initialIds);
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  /** いま選んでいるグループの機能の和集合 (カタログの定義順)。 */
  const previewFeatures = useMemo(() => {
    const union = new Set<string>();
    for (const group of groups) {
      if (selectedSet.has(group.group_id)) group.features.forEach((key) => union.add(key));
    }
    return featureLabels(
      features.map((f) => f.key).filter((key) => union.has(key)),
      features,
    );
  }, [features, groups, selectedSet]);

  /** 保存済みでもカタログに無いグループ (他者が削除済み) の所属は送らない。 */
  const knownGroupIds = useMemo(() => new Set(groups.map((g) => g.group_id)), [groups]);

  const toggleGroup = useCallback((groupId: string, checked: boolean): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(groupId);
      else next.delete(groupId);
      return [...next];
    });
    setErrorMessage(null);
  }, []);

  const executeSave = useCallback(async (): Promise<void> => {
    setSaving(true);
    setErrorMessage(null);
    // モーダルを開いている間にグループが消えた場合を考慮して、一覧にある ID だけを送る。
    const groupIds = selected.filter((id) => knownGroupIds.has(id));
    try {
      await apiClient.setPermissionGroups(user.user_sub, groupIds);
      onSaved(user, groupIds.length);
    } catch (err) {
      setErrorMessage(describeAdminError(err).message);
      setSaving(false);
    }
  }, [apiClient, knownGroupIds, onSaved, selected, user]);

  /** 他者がこのユーザーの所属を変更 / ユーザーを削除していないかを、最新の所属と比べて判定する。 */
  const fetchRemoteState = useCallback(async (): Promise<RemoteChangeState> => {
    try {
      const current = await apiClient.getPermissionGroups(user.user_sub);
      // 削除済みのグループは、サーバが返す最新の所属には含まれない。実在するグループだけで比べて、
      // 他者が何も変更していないのに「他者が更新しています」と誤検出しないようにする。
      const existing = (ids: readonly string[]): string[] =>
        ids.filter((id) => knownGroupIds.has(id));
      return idsKey(existing(current.permission_groups.map((g) => g.group_id))) ===
        idsKey(existing(initialIds))
        ? 'unchanged'
        : 'modified';
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return 'deleted';
      throw err;
    }
  }, [apiClient, initialIds, knownGroupIds, user.user_sub]);

  const handleSave = useCallback((): void => {
    const run = (): void => {
      void guardedUpdate({
        hasChanges: dirty,
        fetchRemoteState,
        execute: () => void executeSave(),
        onDeleted: onDeletedByOthers,
      });
    };
    // 所属をすべて外す (= 権限なしにする) ときは、保存前に実行確認を出す。
    if (selected.length === 0 && initialIds.length > 0) {
      confirmExecute(run);
      return;
    }
    run();
  }, [
    confirmExecute,
    dirty,
    executeSave,
    fetchRemoteState,
    guardedUpdate,
    initialIds.length,
    onDeletedByOthers,
    selected.length,
  ]);

  const handleClose = useCallback((): void => {
    if (dirty) {
      confirmDiscard(onClose);
      return;
    }
    onClose();
  }, [confirmDiscard, dirty, onClose]);

  return (
    <AdminFormModal
      testIdPrefix="permission-assign"
      title={`権限付与: ${userDisplayName(user)}`}
      saveLabel="保存"
      saving={saving}
      errorMessage={errorMessage}
      onDismissError={() => setErrorMessage(null)}
      onSubmit={handleSave}
      onClose={handleClose}
      overlay={confirmDialog}
    >
      <fieldset className="permission-feature-fieldset" data-testid="permission-assign-groups">
        <legend>所属する権限グループ (複数選択可)</legend>
        {groups.length === 0 ? (
          <p className="permission-feature-empty" data-testid="permission-assign-no-groups">
            権限グループがありません。先に「権限グループ管理」画面でグループを作成してください。
          </p>
        ) : null}
        <div className="permission-assign-list">
          {groups.map((group) => (
            <label
              key={group.group_id}
              className="route-id-checkboxes__item permission-assign-item"
            >
              <input
                type="checkbox"
                data-testid={`permission-assign-group-${group.group_id}`}
                checked={selectedSet.has(group.group_id)}
                disabled={saving}
                onChange={(e) => toggleGroup(group.group_id, e.target.checked)}
              />
              <span>
                {group.name}
                {group.description ? (
                  <small className="permission-assign-item__desc">{group.description}</small>
                ) : null}
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      <div className="permission-assign-preview" data-testid="permission-assign-preview">
        <strong>利用できる機能 (選択したグループの合計)</strong>
        <div data-testid="permission-assign-preview-features">
          {previewFeatures.length === 0 ? 'なし (権限なし)' : previewFeatures.join('、')}
        </div>
      </div>
      <small>
        グループを 1
        つも選ばないと、このユーザーは何も利用できなくなります。最後の管理者から管理権限を外す操作は拒否されます。
      </small>
    </AdminFormModal>
  );
}
